// ===== Packages =====

import {
    MutableRefObject,
    useEffect,
    useRef,
}                               from 'react';

// ===== Services =====

import getOrCreateAudioClip     from './getOrCreateAudioClip';

// ===== Interfaces =====

import {
    IAudioClipConfig,
    IAudioClipRefData,
}                               from '../interfaces';

// ===== Enums =====

import { AUDIO_PRELOAD_MODE }   from '../enums';

/**
 * Lazily exposes a reusable UI audio clip ref so components can declare sound
 * dependencies up front without allocating a browser media player on mount.
 *
 * Allocation is delegated to `getOrCreateAudioClip`, while this hook owns the
 * React-specific concerns: applying config changes and cleaning up on unmount.
 */
const useLazyAudioRef = (data: IAudioClipConfig): MutableRefObject<HTMLAudioElement> => {
    const {
        src = '',
        volume = 1,
        loop = false,
        preload = AUDIO_PRELOAD_MODE.auto,
    } = data;

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const appliedConfigRef = useRef<Required<IAudioClipConfig> | null>(null);
    const lazyRef = useRef<MutableRefObject<HTMLAudioElement> | null>(null);

    /**
     * Only reapplies config when the declared source settings change so runtime
     * updates like `currentTime` or temporary volume fades are preserved.
     */
    const applyAudioConfig = (shouldCreate = false): void => {
        const audioClipData: IAudioClipRefData = {
            audioRef,
            src,
            volume,
            loop,
            preload,
        };
        const audioClip = shouldCreate
            ? getOrCreateAudioClip(audioClipData)
            : audioRef.current;

        if (!audioClip) return;

        const nextConfig: Required<IAudioClipConfig> = {
            src,
            volume,
            loop,
            preload,
        };
        const appliedConfig = appliedConfigRef.current;

        if (!appliedConfig || appliedConfig.src !== nextConfig.src) audioClip.src = nextConfig.src;
        if (!appliedConfig || appliedConfig.volume !== nextConfig.volume) audioClip.volume = nextConfig.volume;
        if (!appliedConfig || appliedConfig.loop !== nextConfig.loop) audioClip.loop = nextConfig.loop;
        if (!appliedConfig || appliedConfig.preload !== nextConfig.preload) audioClip.preload = nextConfig.preload;

        appliedConfigRef.current = nextConfig;
    };

    if (!lazyRef.current) {
        const refObject = {} as MutableRefObject<HTMLAudioElement>;

        Object.defineProperty(refObject, 'current', {
            configurable: true,
            enumerable: true,
            get() {
                applyAudioConfig(true);

                return audioRef.current;
            },
            set(value: HTMLAudioElement) {
                audioRef.current = value;

                if (audioRef.current) {
                    applyAudioConfig();
                    return;
                }

                appliedConfigRef.current = null;
            },
        });

        lazyRef.current = refObject;
    }

    useEffect(() => {
        if (!audioRef.current) return;

        applyAudioConfig();
    }, [src, volume, loop, preload]);

    const cleanupAudioClip = (): void => {
        const audioClip = audioRef.current;

        if (!audioClip) return;

        audioClip.pause();
        audioClip.removeAttribute('src');
        audioClip.load();
        audioClip.remove();
        audioRef.current = null;
        appliedConfigRef.current = null;
    };

    useEffect(() => cleanupAudioClip, []);

    return lazyRef.current;
};

export default useLazyAudioRef;
